import React from 'react';
import { DocumentTextIcon, MegaphoneIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';

import { RadioCard, RadioGroup, Tooltip } from '../../../components/ui';
import { cn } from '../../../shared/lib/cn';
import { BVTemplateType } from '../types';
import { BV_TEMPLATE_OPTIONS } from '../utils';

interface TemplateSelectorProps {
  value: BVTemplateType;
  onChange: (value: BVTemplateType) => void;
  disabled?: boolean;
  compact?: boolean;
}

function getTemplateIcon(templateType: BVTemplateType) {
  switch (templateType) {
    case 'search_ad':
    case 'comparative':
      return <MegaphoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    case 'pdp_snippet':
    case 'retail_buyer_kit':
      return <ShoppingBagIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    case 'social_ad':
    case 'email_snippet':
    default:
      return <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
  }
}

export function TemplateSelector({ value, onChange, disabled = false, compact = false }: TemplateSelectorProps) {
  return (
    <div className={cn('space-y-3', compact && 'space-y-2')}>
      <div>
        <p className="text-sm font-medium text-charcoal dark:text-gray-100">Template</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Choose the content format the draft should be optimized for.
        </p>
      </div>

      <RadioGroup
        value={value}
        onValueChange={nextValue => onChange(nextValue as BVTemplateType)}
        className={cn(
          compact ? 'grid grid-cols-1 gap-2 min-[1920px]:grid-cols-2' : 'grid gap-3 md:grid-cols-2',
        )}
        disabled={disabled}
      >
        {BV_TEMPLATE_OPTIONS.map(option => (
          <RadioCard key={option.value} value={option.value} className={compact ? 'p-3' : undefined}>
            <div className={cn('space-y-3', compact && 'space-y-2')}>
              <div className="flex items-center gap-3">
                <div className={cn('rounded-lg bg-gray-100 p-2 dark:bg-dark-surface-2', compact && 'p-1.5')}>
                  {getTemplateIcon(option.value)}
                </div>
                <div>
                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">{option.label}</p>
                </div>
              </div>
              {compact ? (
                <Tooltip content={option.description} position="top">
                  <p className="line-clamp-2 min-h-8 text-xs leading-4 text-gray-500 dark:text-gray-400">
                    {option.description}
                  </p>
                </Tooltip>
              ) : (
                <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{option.description}</p>
              )}
            </div>
          </RadioCard>
        ))}
      </RadioGroup>
    </div>
  );
}

export default TemplateSelector;
